import React, { useState, useEffect } from 'react';
import PostmanConnect from './PostmanConnect';
import { 
    getCollections, 
    getCollectionEndpoints, 
    testEndpoint,
    generateWebhookTest,
    connectPostman,
    getPostmanWorkspaces,
    getPostmanCollections,
    syncPostmanCollection,
    deleteCollection
} from '../services/api';

export default function ApiExplorer() {
    const [collections, setCollections] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState('synced');
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [endpoints, setEndpoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPostmanConnected, setIsPostmanConnected] = useState(false);
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [syncingCollection, setSyncingCollection] = useState(null);
    const [expandedCollections, setExpandedCollections] = useState(new Set());
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [availableCollections, setAvailableCollections] = useState([]);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [collectionEndpoints, setCollectionEndpoints] = useState({}); // Store endpoints by collection
    
    // Test state
    const [testResult, setTestResult] = useState(null);
    const [requestConfig, setRequestConfig] = useState({
        method: 'GET',
        url: '',
        headers: {},
        body: '',
        auth_required: true
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (selectedWorkspace === 'synced') {
            loadSyncedCollections();
        } else if (selectedWorkspace && selectedWorkspace !== 'synced') {
            loadWorkspaceCollections(selectedWorkspace);
        }
    }, [selectedWorkspace]);

    const loadData = async () => {
        await loadSyncedCollections();
        await checkPostmanConnection();
    };

    const checkPostmanConnection = async () => {
        try {
            const response = await getPostmanWorkspaces();
            setIsPostmanConnected(true);
            setWorkspaces(response.data.workspaces || []);
        } catch {
            setIsPostmanConnected(false);
        }
    };

    const loadSyncedCollections = async () => {
        try {
            setLoadingCollections(true);
            const response = await getCollections();
            setCollections(response.data.collections || []);
        } catch (err) {
            console.error('Error loading collections:', err);
        } finally {
            setLoadingCollections(false);
        }
    };

    const loadWorkspaceCollections = async (workspaceId) => {
        try {
            setLoadingCollections(true);
            const response = await getPostmanCollections(workspaceId === 'all' ? null : workspaceId);
            setAvailableCollections(response.data.collections || []);
        } catch (err) {
            console.error('Error loading workspace collections:', err);
        } finally {
            setLoadingCollections(false);
        }
    };

    const toggleCollection = async (collection) => {
        const newExpanded = new Set(expandedCollections);
        
        if (newExpanded.has(collection.id)) {
            newExpanded.delete(collection.id);
            setSelectedCollection(null);
        } else {
            newExpanded.add(collection.id);
            setSelectedCollection(collection);
            
            // Load endpoints if not already loaded
            if (!collectionEndpoints[collection.id]) {
                try {
                    setLoading(true);
                    const response = await getCollectionEndpoints(collection.id);
                    setCollectionEndpoints(prev => ({
                        ...prev,
                        [collection.id]: response.data.endpoints || []
                    }));
                } catch (err) {
                    console.error('Error loading endpoints:', err);
                } finally {
                    setLoading(false);
                }
            }
        }
        
        setExpandedCollections(newExpanded);
    };

    const toggleFolder = (folderId) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId);
        } else {
            newExpanded.add(folderId);
        }
        setExpandedFolders(newExpanded);
    };

    const handleSelectEndpoint = (endpoint, collection) => {
        setSelectedEndpoint(endpoint);
        setSelectedCollection(collection);
        setRequestConfig({
            method: endpoint.method,
            url: endpoint.url,
            headers: endpoint.headers || {},
            body: endpoint.body_example || '',
            auth_required: endpoint.auth_required
        });
    };

    const handleSyncCollection = async (collectionUid, collectionName) => {
    setSyncingCollection(collectionUid);
    try {
        await syncPostmanCollection(collectionUid);
        alert(`Successfully synced: ${collectionName}`);
        if (selectedWorkspace === 'synced') {
            await loadSyncedCollections();
        }
    } catch (err) {
        console.error('Sync error:', err);
        
        let errorMessage = 'Failed to sync collection';
        if (err.response?.status === 504) {
            errorMessage = 'Collection sync timed out. The collection might be too large. Try syncing smaller collections.';
        } else if (err.response?.status === 413) {
            errorMessage = 'Collection is too large to sync. Please split it into smaller collections.';
        } else if (err.response?.data?.detail) {
            errorMessage = err.response.data.detail;
        }
        
        alert(errorMessage);
    } finally {
        setSyncingCollection(null);
    }
};

    const handleDeleteCollection = async (collectionId) => {
        if (window.confirm('Are you sure you want to delete this collection?')) {
            try {
                await deleteCollection(collectionId);
                await loadSyncedCollections();
                
                // Clean up state
                setCollectionEndpoints(prev => {
                    const newState = { ...prev };
                    delete newState[collectionId];
                    return newState;
                });
                
                if (selectedCollection?.id === collectionId) {
                    setSelectedCollection(null);
                    setSelectedEndpoint(null);
                }
            } catch (err) {
                alert('Failed to delete collection');
            }
        }
    };

    const handleTestEndpoint = async () => {
        try {
            setLoading(true);
            setTestResult(null);
            
            const response = await testEndpoint(requestConfig);
            setTestResult(response.data);
        } catch (err) {
            setTestResult({
                error: true,
                message: err.response?.data?.detail || 'Test failed'
            });
        } finally {
            setLoading(false);
        }
    };

    // Group endpoints by folder path
    const groupEndpointsByFolder = (endpoints) => {
        const grouped = {};
        
        endpoints.forEach(endpoint => {
            const folderPath = endpoint.folder_path || 'General';
            if (!grouped[folderPath]) {
                grouped[folderPath] = [];
            }
            grouped[folderPath].push(endpoint);
        });
        
        return grouped;
    };

    // Render collection tree structure
    const renderCollectionTree = (collection) => {
        const isExpanded = expandedCollections.has(collection.id);
        const endpoints = collectionEndpoints[collection.id] || [];
        const groupedEndpoints = groupEndpointsByFolder(endpoints);
        
        return (
            <div key={collection.id} className="mb-1">
                {/* Collection Header */}
                <div
                    className={`flex items-center px-2 py-1.5 hover:bg-gray-100 cursor-pointer rounded group ${
                        selectedCollection?.id === collection.id ? 'bg-gray-100' : ''
                    }`}
                    onClick={() => toggleCollection(collection)}
                >
                    <span className="text-gray-400 text-xs mr-1">
                        {isExpanded ? '▼' : '▶'}
                    </span>
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <span className="text-sm font-medium flex-1">{collection.name}</span>
                    <span className="text-xs text-gray-500 mr-2">{collection.endpoints_count}</span>
                    
                    {/* Sync status for synced collections */}
                    {collection.sync_count && (
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSyncCollection(collection.uid, collection.name);
                                }}
                                className="p-1 hover:bg-gray-200 rounded"
                                title="Re-sync"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCollection(collection.id);
                                }}
                                className="p-1 hover:bg-gray-200 rounded"
                                title="Delete"
                            >
                                <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Folders and Endpoints */}
                {isExpanded && (
                    <div className="ml-4">
                        {loading && endpoints.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
                        ) : (
                            Object.entries(groupedEndpoints).map(([folderPath, folderEndpoints]) => {
                                const folderId = `${collection.id}-${folderPath}`;
                                const isFolderExpanded = expandedFolders.has(folderId);
                                const folderName = folderPath.split('/').pop() || folderPath;
                                
                                return (
                                    <div key={folderId} className="mb-1">
                                        {/* Folder */}
                                        <div
                                            className="flex items-center px-2 py-1 hover:bg-gray-100 cursor-pointer rounded"
                                            onClick={() => toggleFolder(folderId)}
                                        >
                                            <span className="text-gray-400 text-xs mr-1">
                                                {isFolderExpanded ? '▼' : '▶'}
                                            </span>
                                            <svg className="w-3 h-3 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                            </svg>
                                            <span className="text-sm text-gray-700">{folderName}</span>
                                            <span className="text-xs text-gray-500 ml-auto">{folderEndpoints.length}</span>
                                        </div>
                                        
                                        {/* Endpoints */}
                                        {isFolderExpanded && (
                                            <div className="ml-4">
                                                {folderEndpoints.map(endpoint => (
                                                    <div
                                                        key={endpoint.id}
                                                        className={`flex items-center px-2 py-1 hover:bg-gray-100 cursor-pointer rounded group text-sm ${
                                                            selectedEndpoint?.id === endpoint.id ? 'bg-blue-50' : ''
                                                        }`}
                                                        onClick={() => handleSelectEndpoint(endpoint, collection)}
                                                    >
                                                        <span className={`px-1.5 py-0.5 text-xs font-semibold rounded mr-2 ${
                                                            getMethodColor(endpoint.method)
                                                        }`}>
                                                            {endpoint.method}
                                                        </span>
                                                        <span className="flex-1 truncate">{endpoint.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render available collection for syncing
    const renderAvailableCollection = (collection) => {
        return (
            <div key={collection.uid} className="flex items-center px-2 py-2 hover:bg-gray-100 rounded group">
                <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <div className="flex-1">
                    <div className="text-sm font-medium">{collection.name}</div>
                    {collection.owner && (
                        <div className="text-xs text-gray-500">Owner: {collection.owner}</div>
                    )}
                </div>
                <button
                    onClick={() => handleSyncCollection(collection.uid, collection.name)}
                    disabled={syncingCollection === collection.uid}
                    className="opacity-0 group-hover:opacity-100 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                    {syncingCollection === collection.uid ? 'Syncing...' : 'Sync'}
                </button>
            </div>
        );
    };

    return (
        <div className="h-screen flex bg-gray-50">
            {/* Left Sidebar - Workspaces & Collections */}
            <div className="w-80 bg-white border-r flex flex-col h-full">
                {/* Sidebar Header */}
                <div className="p-4 border-b">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg">Collections</h3>
                        {!isPostmanConnected && (
                            <button
                                onClick={() => setShowConnectModal(true)}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                Connect
                            </button>
                        )}
                    </div>
                    
                    {/* Connection Status */}
                    <div className="text-xs">
                        {isPostmanConnected ? (
                            <div className="flex items-center justify-between">
                                <span className="flex items-center text-green-600">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    Postman Connected
                                </span>
                                <button
                                    onClick={() => setShowConnectModal(true)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    Settings
                                </button>
                            </div>
                        ) : (
                            <span className="text-gray-500">Postman not connected</span>
                        )}
                    </div>
                </div>

                {/* Workspace Selector */}
                <div className="px-4 py-3 border-b bg-gray-50">
                    <label className="block text-xs font-medium text-gray-700 mb-2">Select Workspace</label>
                    <select
                        value={selectedWorkspace}
                        onChange={(e) => {
                            setSelectedWorkspace(e.target.value);
                            setSelectedCollection(null);
                            setSelectedEndpoint(null);
                            setExpandedCollections(new Set());
                            setExpandedFolders(new Set());
                        }}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                        <option value="synced">📁 Synced Collections</option>
                        {isPostmanConnected && workspaces.length > 0 && (
                            <>
                                <optgroup label="Postman Workspaces">
                                    <option value="all">All Workspaces</option>
                                    {workspaces.map(ws => (
                                        <option key={ws.id} value={ws.id}>
                                            {ws.name} ({ws.type})
                                        </option>
                                    ))}
                                </optgroup>
                            </>
                        )}
                    </select>
                </div>

                {/* Collections Tree */}
                <div className="flex-1 overflow-y-auto p-2">
                    {loadingCollections ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">Loading collections...</p>
                        </div>
                    ) : (
                        <>
                            {selectedWorkspace === 'synced' ? (
                                collections.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p className="text-sm mb-4">No collections synced yet</p>
                                        {isPostmanConnected && (
                                            <button
                                                onClick={() => setSelectedWorkspace('all')}
                                                className="text-sm text-blue-600 hover:text-blue-800"
                                            >
                                                Browse Postman Collections
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    collections.map(collection => renderCollectionTree(collection))
                                )
                            ) : (
                                availableCollections.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p className="text-sm">No collections in this workspace</p>
                                    </div>
                                ) : (
                                    availableCollections.map(collection => renderAvailableCollection(collection))
                                )
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col">
                {selectedEndpoint && selectedCollection ? (
                    <>
                        {/* Breadcrumb */}
                        <div className="bg-white border-b px-4 py-3">
                            <div className="flex items-center text-sm text-gray-600">
                                <span className="font-medium text-gray-900">{selectedCollection.name}</span>
                                <span className="mx-2">/</span>
                                <span>{selectedEndpoint.folder_path || 'General'}</span>
                                <span className="mx-2">/</span>
                                <span className="text-gray-900">{selectedEndpoint.name}</span>
                            </div>
                        </div>

                        {/* Endpoint Details */}
                        <div className="flex-1 bg-gray-50">
                            <EndpointDetails
                                endpoint={selectedEndpoint}
                                requestConfig={requestConfig}
                                setRequestConfig={setRequestConfig}
                                onTest={handleTestEndpoint}
                                testResult={testResult}
                                loading={loading}
                            />
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center text-gray-500">
                            <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            <p className="text-lg mb-2">
                                {selectedWorkspace === 'synced' 
                                    ? 'Select a collection to explore'
                                    : 'Select a collection to sync'}
                            </p>
                            {!isPostmanConnected && (
                                <button
                                    onClick={() => setShowConnectModal(true)}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Connect Postman Account
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Connect Modal */}
            {showConnectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowConnectModal(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Postman Settings</h3>
                            <button
                                onClick={() => setShowConnectModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="max-h-[80vh] overflow-y-auto">
                            <PostmanConnect 
                                onCollectionSynced={() => {
                                    loadData();
                                    setShowConnectModal(false);
                                }}
                                isConnected={isPostmanConnected}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Keep your existing EndpointDetails c omponent and getMethodColor function
function EndpointDetails({ endpoint, requestConfig, setRequestConfig, onTest, testResult, loading }) {
    const [activeTab, setActiveTab] = useState('overview');

    return (
        <div className="h-full flex flex-col">
            {/* Tabs */}
            <div className="bg-white border-b">
                <nav className="flex px-4">
                    {['overview', 'test', 'events'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-3 text-sm font-medium capitalize border-b-2 ${
                                activeTab === tab
                                    ? 'text-blue-600 border-blue-600'
                                    : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold mb-2">{endpoint.name}</h3>
                            <div className="flex items-center gap-3 mb-4">
                                <span className={`px-3 py-1 text-sm font-semibold rounded ${
                                    getMethodColor(endpoint.method)
                                }`}>
                                    {endpoint.method}
                                </span>
                                <code className="text-sm bg-gray-100 px-3 py-1 rounded flex-1">
                                    {endpoint.url}
                                </code>
                            </div>
                            {endpoint.description && (
                                <p className="text-gray-600">{endpoint.description}</p>
                            )}
                        </div>

                        {endpoint.headers && endpoint.headers.length > 0 && (
                            <div>
                                <h4 className="font-medium mb-2">Headers</h4>
                                <div className="bg-gray-50 rounded p-3">
                                    {endpoint.headers.map((header, idx) => (
                                        <div key={idx} className="text-sm font-mono">
                                            <span className="text-blue-600">{header.key}</span>: {header.value}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <h4 className="font-medium mb-2">Entity & Operation</h4>
                            <div className="flex gap-2">
                                <span className="px-3 py-1 bg-gray-100 rounded text-sm">
                                    Entity: <strong>{endpoint.entity}</strong>
                                </span>
                                <span className="px-3 py-1 bg-gray-100 rounded text-sm">
                                    Operation: <strong>{endpoint.operation_type}</strong>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'test' && (
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium mb-4">Request</h4>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Method & URL</label>
                                    <div className="flex gap-2">
                                        <select
                                            value={requestConfig.method}
                                            onChange={(e) => setRequestConfig({...requestConfig, method: e.target.value})}
                                            className="px-3 py-2 border rounded"
                                        >
                                            <option value="GET">GET</option>
                                            <option value="POST">POST</option>
                                            <option value="PUT">PUT</option>
                                            <option value="PATCH">PATCH</option>
                                            <option value="DELETE">DELETE</option>
                                        </select>
                                        <input
                                            type="text"
                                            value={requestConfig.url}
                                            onChange={(e) => setRequestConfig({...requestConfig, url: e.target.value})}
                                            className="flex-1 px-3 py-2 border rounded"
                                        />
                                    </div>
                                </div>

                                {['POST', 'PUT', 'PATCH'].includes(requestConfig.method) && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Body</label>
                                        <textarea
                                            value={requestConfig.body}
                                            onChange={(e) => setRequestConfig({...requestConfig, body: e.target.value})}
                                            className="w-full px-3 py-2 border rounded font-mono text-sm"
                                            rows={8}
                                            placeholder='{"key": "value"}'
                                        />
                                    </div>
                                )}

                                <button
                                    onClick={onTest}
                                    disabled={loading}
                                    className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                >
                                    {loading ? 'Testing...' : 'Send Request'}
                                </button>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-medium mb-4">Response</h4>
                            
                            {testResult && (
                                <div className="space-y-4">
                                    {testResult.error ? (
                                        <div className="p-4 bg-red-50 text-red-700 rounded">
                                            {testResult.message}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <span className={`px-3 py-1 rounded text-white ${
                                                    testResult.status_code < 400 ? 'bg-green-500' : 'bg-red-500'
                                                }`}>
                                                    {testResult.status_code}
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    {testResult.response_time}s
                                                </span>
                                            </div>
                                            
                                            <div>
                                                <div className="text-sm font-medium mb-1">Body:</div>
                                                <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                                                    {JSON.stringify(testResult.body, null, 2)}
                                                </pre>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'events' && (
                    <div>
                        <h4 className="font-medium mb-4">Potential Events</h4>
                        
                        {endpoint.potential_events?.length > 0 ? (
                            <div className="space-y-3">
                                {endpoint.potential_events.map(event => (
                                    <div key={event} className="bg-purple-50 p-4 rounded">
                                        <div className="font-medium text-purple-900 mb-2">{event}</div>
                                        <pre className="text-xs bg-white p-3 rounded overflow-x-auto">
{JSON.stringify({
    event: event,
    timestamp: new Date().toISOString(),
    data: {
        id: "123",
        // Additional fields based on entity
    },
    metadata: {
        source: "dentulu_api",
        user_id: "user_456"
    }
}, null, 2)}
                                        </pre>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500">No events detected for this endpoint</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function getMethodColor(method) {
    const colors = {
        GET: 'bg-green-500 text-white',
        POST: 'bg-blue-500 text-white',
        PUT: 'bg-orange-500 text-white',
        PATCH: 'bg-yellow-500 text-white',
        DELETE: 'bg-red-500 text-white'
    };
    return colors[method] || 'bg-gray-500 text-white';
}